import React, { useEffect, useState } from 'react';
import dashboardEmendaService, {
  type DashboardEmendaResponseDTO,
  type VereadorDashboardDTO,
} from '../services/dashboardEmendaService';

const brlk = (v: number): string => {
  if (v >= 1_000_000) return `R$ ${(v / 1_000_000).toFixed(1).replace('.', ',')}M`;
  if (v >= 1_000) return `R$ ${Math.round(v / 1_000)}k`;
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);
};

const brl = (v: number): string =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);

const ProgressBar = ({ pct, colorClass }: { pct: number; colorClass: string }) => (
  <div className="mt-0.5 h-1 w-10 rounded-full bg-slate-200 dark:bg-slate-700">
    <div className={`h-1 rounded-full ${colorClass}`} style={{ width: `${Math.min(pct, 100)}%` }} />
  </div>
);

interface KpiCardProps {
  label: string;
  value: string;
  valueClass?: string;
}

const KpiCard = ({ label, value, valueClass = 'text-slate-900 dark:text-slate-100' }: KpiCardProps) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
    <p className="text-xs font-medium text-slate-500 dark:text-slate-400">{label}</p>
    <p className={`mt-1 text-2xl font-bold ${valueClass}`}>{value}</p>
  </div>
);

interface Props {
  isVereador: boolean;
  defaultCollapsed?: boolean;
}


const BoxEmendaVereador: React.FC<Props> = ({ isVereador, defaultCollapsed = false }) => {
  const [anos, setAnos] = useState<number[]>([]);
  const [anoSelecionado, setAnoSelecionado] = useState<number>(new Date().getFullYear());
  const [data, setData] = useState<DashboardEmendaResponseDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [collapsed, setCollapsed] = useState(defaultCollapsed);

  useEffect(() => {
    dashboardEmendaService.getAnosDisponiveis()
      .then(lista => {
        setAnos(lista);
        if (lista.length > 0 && !lista.includes(anoSelecionado)) {
          setAnoSelecionado(lista[0]);
        }
      })
      .catch(() => setAnos([new Date().getFullYear()]));
  }, []);

  useEffect(() => {
    setLoading(true);
    setError('');
    dashboardEmendaService.getConsolidado(anoSelecionado)
      .then(setData)
      .catch(e => setError(e?.response?.data?.error ?? 'Erro ao carregar dados de emendas.'))
      .finally(() => setLoading(false));
  }, [anoSelecionado]);

  const renderRow = (categorias: string[], v: VereadorDashboardDTO) => {
    const hasSaude = categorias.includes('SAUDE');
    const hasEducacao = categorias.includes('EDUCACAO');
    return (
      <tr key={v.parlamentarId} className="border-b border-slate-100 last:border-0 dark:border-slate-800">
        <td className="px-3 py-2 align-middle">
          <p className="text-xs font-semibold leading-tight text-slate-900 dark:text-slate-100">{v.nome}</p>
          <p className="text-[10px] text-slate-500 dark:text-slate-400">{v.partido}</p>
        </td>
        <td className="px-2 py-2 text-right text-xs text-slate-700 align-middle whitespace-nowrap dark:text-slate-300">
          {brl(v.limiteAnual)}
        </td>
        {hasSaude && (
          <td className="px-2 py-2 text-right text-xs align-middle whitespace-nowrap">
            <span className="font-medium text-green-700">{brl(v.valorSaude)}</span>
          </td>
        )}
        {hasEducacao && (
          <td className="px-2 py-2 text-right text-xs align-middle whitespace-nowrap">
            <span className="font-medium text-purple-700">{brl(v.valorEducacao)}</span>
          </td>
        )}
        <td className="px-2 py-2 text-right text-xs text-slate-700 align-middle whitespace-nowrap dark:text-slate-300">
          {brl(v.valorOutras)}
        </td>
        {hasSaude && (
          <td className="px-2 py-2 align-middle">
            <div className="flex flex-col items-end">
              <span className={`text-xs font-medium ${v.alertaSaude ? 'text-red-600' : 'text-green-700'}`}>
                {v.percentualSaude.toFixed(0)}%
              </span>
              <ProgressBar pct={v.percentualSaude} colorClass={v.alertaSaude ? 'bg-red-500' : 'bg-green-500'} />
            </div>
          </td>
        )}
        {hasEducacao && (
          <td className="px-2 py-2 align-middle">
            <div className="flex flex-col items-end">
              <span className={`text-xs font-medium ${v.alertaEducacao ? 'text-red-600' : 'text-purple-700'}`}>
                {v.percentualEducacao.toFixed(0)}%
              </span>
              <ProgressBar pct={v.percentualEducacao} colorClass={v.alertaEducacao ? 'bg-red-500' : 'bg-purple-500'} />
            </div>
          </td>
        )}
        <td className="px-2 py-2 align-middle">
          <div className="flex flex-col items-end">
            <span className="text-xs font-medium text-blue-700">
              {v.percentualLimite.toFixed(0)}%
            </span>
            <ProgressBar pct={v.percentualLimite} colorClass="bg-blue-500" />
          </div>
        </td>
      </tr>
    );
  };

  return (
    <div className="h-fit overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-950">
      {/* Cabeçalho do box */}
      <div
        className="flex cursor-pointer flex-wrap items-center justify-between gap-3 border-b border-slate-200 bg-slate-50 px-5 py-4 transition-colors hover:bg-slate-100 dark:border-slate-800 dark:bg-slate-900 dark:hover:bg-slate-800"
        onClick={() => setCollapsed(v => !v)}
      >
        <div>
          <h2 className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-600 dark:text-slate-300">
            Emendas por Vereador
          </h2>
          <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
            Distribuição por área de atuação
          </p>
        </div>
        <div className="flex items-center gap-3">
          {!collapsed && (
            <select
              value={anoSelecionado}
              onChange={e => setAnoSelecionado(Number(e.target.value))}
              onClick={e => e.stopPropagation()}
              className="rounded-xl border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 focus:border-indigo-400 focus:outline-none dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
            >
              {anos.map(a => (
                <option key={a} value={a}>{a}</option>
              ))}
            </select>
          )}
          <span className="rounded-lg border border-slate-300 bg-white px-3 py-1 text-xs font-medium text-slate-700 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200">
            {collapsed ? 'Expandir' : 'Recolher'}
          </span>
        </div>
      </div>

      {!collapsed && (
        loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="h-7 w-7 animate-spin rounded-full border-b-2 border-indigo-600" />
          </div>
        ) : error ? (
          <div className="px-5 py-6 text-sm text-red-600">{error}</div>
        ) : !data ? null : (() => {
          const categorias = data.categoriasAtivas ?? [];
          const hasSaude = categorias.includes('SAUDE');
          const hasEducacao = categorias.includes('EDUCACAO');

          return (
            <div className="space-y-5 p-5">
              {/* KPI row 1 — totais globais */}
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <KpiCard label="Total empenhado" value={brlk(data.kpis.totalEmpenhado)} />
                {hasSaude && (
                  <KpiCard
                    label="Total em Saúde"
                    value={brlk(data.kpis.totalSaude)}
                    valueClass="text-green-700"
                  />
                )}
                {hasEducacao && (
                  <KpiCard
                    label="Total em Educação"
                    value={brlk(data.kpis.totalEducacao)}
                    valueClass="text-purple-700"
                  />
                )}
                <KpiCard label="Outras áreas" value={brlk(data.kpis.totalOutras)} />
                {hasSaude && (
                  <KpiCard
                    label="% Saúde geral"
                    value={`${data.kpis.percentualSaudeGeral.toFixed(0)}%`}
                    valueClass="text-green-700"
                  />
                )}
                {hasEducacao && (
                  <KpiCard
                    label="% Educação geral"
                    value={`${data.kpis.percentualEducacaoGeral.toFixed(0)}%`}
                    valueClass="text-purple-700"
                  />
                )}
              </div>

              {/* KPI row 2 — contadores */}
              <div className="grid grid-cols-3 gap-3">
                <KpiCard
                  label="Cota atingida"
                  value={String(data.kpis.countCotaAtingida)}
                  valueClass="text-green-700"
                />
                <KpiCard
                  label="Alerta"
                  value={String(data.kpis.countAlerta)}
                  valueClass={data.kpis.countAlerta > 0 ? 'text-orange-600' : 'text-slate-900 dark:text-slate-100'}
                />
                <KpiCard
                  label="Pendente ciência"
                  value={String(data.kpis.countPendente)}
                  valueClass={data.kpis.countPendente > 0 ? 'text-orange-600' : 'text-slate-900 dark:text-slate-100'}
                />
              </div>

              {/* Tabela de parlamentares */}
              {data.vereadores.length === 0 ? (
                <p className="py-4 text-center text-sm text-slate-500">
                  Nenhum dado de emenda encontrado para {anoSelecionado}.
                </p>
              ) : (
                <div className="overflow-x-auto rounded-2xl border border-slate-200 dark:border-slate-800">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-900">
                        <th className="px-3 py-2 text-left font-semibold text-slate-700 dark:text-slate-300">Parlamentar</th>
                        <th className="px-2 py-2 text-right font-semibold text-slate-700 whitespace-nowrap dark:text-slate-300">Limite anual</th>
                        {hasSaude && (
                          <th className="px-2 py-2 text-right font-semibold text-green-700 whitespace-nowrap">Saúde</th>
                        )}
                        {hasEducacao && (
                          <th className="px-2 py-2 text-right font-semibold text-purple-700 whitespace-nowrap">Educação</th>
                        )}
                        <th className="px-2 py-2 text-right font-semibold text-slate-700 whitespace-nowrap dark:text-slate-300">Outras</th>
                        {hasSaude && (
                          <th className="px-2 py-2 text-right font-semibold text-green-700 whitespace-nowrap">% Saúde</th>
                        )}
                        {hasEducacao && (
                          <th className="px-2 py-2 text-right font-semibold text-purple-700 whitespace-nowrap">% Educ.</th>
                        )}
                        <th className="px-2 py-2 text-right font-semibold text-slate-700 whitespace-nowrap dark:text-slate-300">% Limite</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 bg-white dark:divide-slate-800 dark:bg-slate-950">
                      {data.vereadores.map(v => renderRow(categorias, v))}
                    </tbody>
                    <tfoot className="border-t border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-900">
                      <tr>
                        <td className="px-3 py-2 text-xs font-bold text-slate-900 dark:text-slate-100">Total</td>
                        <td className="px-2 py-2 text-right text-xs font-semibold text-slate-700 whitespace-nowrap dark:text-slate-300">
                          {brl(data.vereadores.reduce((total, item) => total + item.limiteAnual, 0))}
                        </td>
                        {hasSaude && (
                          <td className="px-2 py-2 text-right text-xs font-bold text-green-700 whitespace-nowrap">
                            {brl(data.kpis.totalSaude)}
                          </td>
                        )}
                        {hasEducacao && (
                          <td className="px-2 py-2 text-right text-xs font-bold text-purple-700 whitespace-nowrap">
                            {brl(data.kpis.totalEducacao)}
                          </td>
                        )}
                        <td className="px-2 py-2 text-right text-xs font-bold text-slate-800 whitespace-nowrap dark:text-slate-200">
                          {brl(data.kpis.totalOutras)}
                        </td>
                        {hasSaude && (
                          <td className="px-2 py-2 text-right text-xs font-semibold text-green-700 whitespace-nowrap">
                            {data.kpis.percentualSaudeGeral.toFixed(0)}%
                          </td>
                        )}
                        {hasEducacao && (
                          <td className="px-2 py-2 text-right text-xs font-semibold text-purple-700 whitespace-nowrap">
                            {data.kpis.percentualEducacaoGeral.toFixed(0)}%
                          </td>
                        )}
                        <td className="px-2 py-2 text-right text-xs font-semibold text-blue-700 whitespace-nowrap">
                          {(() => {
                            const limiteTotal = data.vereadores.reduce((total, item) => total + item.limiteAnual, 0);
                            return limiteTotal > 0 ? `${((data.kpis.totalEmpenhado / limiteTotal) * 100).toFixed(0)}%` : '0%';
                          })()}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
          );
        })()
      )}
    </div>
  );
};

export default BoxEmendaVereador;
